import React, { Suspense, lazy } from "react";
import { withUserProfile } from "../../../../../../userProfile/userProfileContext";
import { withStyles } from "@material-ui/core";
import { observer, inject } from "mobx-react";
import { autorun } from "mobx";
import { compose } from "recompose";
import { getDataWithQueryString } from "../../../../../../api/componentAPIs";
import DataGrid from '@dataGrid';
import GridConfig from "./GridConfig";
import styles from "./../styles";
import { withSnackbar } from "notistack";
import { csvFormulaInjectionSanitizer } from "../../../../../../utilities/csvUtils";
import StatusBar from '@dataGrid/components/StatusBar';
import Header from "../GridHeader";
import { getSpecialNeedsOptionsWithServiceLevel } from "../../../../utils";
import { withAppConfig } from "../../../../../../appConfigContext";
import { isEqual } from "lodash/lang";
import { AppLoading } from "../../../../../../loaders/loaders";
import Events from "../../../../../../utilities/EventEmitter";
import { getUniqueKeyForStripPriorityRowData } from "../../utils";
import { sortBy } from "lodash";
import appConstants from "../../../../../../appConstants";
const ProImages = lazy(() => import("@ProImages"));

@inject("IBPlanningStore")
@observer
class Shipments extends React.Component {
  constructor(props) {
    super(props);
    this.gridConfig = new GridConfig({
      columnConfig: this.props.configJSON?.gridColumnConfig,
      userConfig: this.props.userData.userProfile?.columnPref
    });
    this.state = {
      proIdList: [],
      showProImagesPopup: false,
      shipmentsData: null,
      quickFilterText: "",
      selectedTrailers: [],
      errorMessage: appConstants.GRID_STATUS.LOADING,
    };
    this.serviceLevelList = this.props.IBPlanningStore.serviceLevelList;
    this.gridContext = {
      hasProImagesURL: this.hasProImagesURL(),
      specialNeedsOptions: getSpecialNeedsOptionsWithServiceLevel(this.serviceLevelList),
    };
    this.gridApi = {};
  }
  onGridReady = ({ api }) => {
    this.gridApi = api;
  };

  componentDidMount() {
    this._isComponentMounted = true;
    this.getStripPriorityShipments();
    this.autorunPriorityShipmentList = autorun(() => {
      if (
        !isEqual(
          this.serviceLevelList,
          this.props.IBPlanningStore.serviceLevelList
        )
      ) {
        this.serviceLevelList = this.props.IBPlanningStore.serviceLevelList;
        this.gridContext = {
          hasProImagesURL: this.hasProImagesURL(),
          specialNeedsOptions: getSpecialNeedsOptionsWithServiceLevel(this.serviceLevelList),
        };
      }
    });
    Events.on("UpdateSelectedTrailersShipments", this.updateTrailersShipments);
    Events.on("RefreshStripPriorityShipments", this.getStripPriorityShipments);
  }

  componentWillUnmount() {
    this._isComponentMounted = false;
    this?.autorunPriorityShipmentList();
    Events.off("UpdateSelectedTrailersShipments", this.updateTrailersShipments);
    Events.off("RefreshStripPriorityShipments", this.getStripPriorityShipments);
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.userData.userProfile.terminal.id !== prevProps.userData.userProfile.terminal.id
    ) {
      this.getStripPriorityShipments();
    }
  }

  updateTrailersShipments = (selectedTrailers) => {
    this.setState({
      selectedTrailers,
    });
  };

  getTrailersShipments = () => {
    return (
      this.state.shipmentsData?.filter((shipment) => {
        const { trailerNumber, trailerScac } = shipment;
        return this.state.selectedTrailers.includes(
          getUniqueKeyForStripPriorityRowData(trailerNumber, trailerScac)
        );
      }) ?? []
    );
  };

  updateQuickFilterText = (searchText) => {
    this.setState({
      quickFilterText: searchText,
    });
  };

  hasProImagesURL = () => {
    return Boolean(this.props.configJSON.proImagesUrl);
  };

  exportCSV = () => {
    if (!this.state.shipmentsData.length || !this.gridApi) {
      return;
    }
    const columnIds = this.gridApi.getColumns()
      .reduce((columnKeys, column) => {
        if (column.getColDef().headerName) {
          columnKeys.push(column.getColId());
        }
        return columnKeys;
      }, []);
    const params = {
      fileName: "Strip_Priority_Report",
      columnKeys: columnIds,
      processCellCallback: (cell) => csvFormulaInjectionSanitizer(cell.value),
    };
    this.gridApi.exportDataAsCsv(params);
  };

  setGridStatusToLoading = () => {
    this.setState({
      shipmentsData: null,
      proIdList: [],
      errorMessage: appConstants.GRID_STATUS.LOADING,
    });
  };

  getStripPriorityShipments = () => {
    this.setGridStatusToLoading();
    const userProfile = this.props.userData?.userProfile;
    const { terminal, planDate } = userProfile || {};
    const { id:terminalId } = terminal || {};
    getDataWithQueryString(
      "GetStripPriorityShipments",
      `planDate=${planDate}&terminalId=${terminalId}`
    ).then(
      (response) => {
        if (!this._isComponentMounted) {
          return;
        }
        if (response.status === 200) {
          this.setState({
            shipmentsData: sortBy(response.data, [
              (shipment) => shipment.routeName,
              (shipment) => shipment.stopSequence,
            ]),
            proIdList: [],
            errorMessage: appConstants.GRID_STATUS.SUCCESS,
          });
          return;
        }
        this.handleError();
      },
      (_error) => {
        if (this._isComponentMounted) {
          this.handleError();
        }
      }
    );
  };

  handleError = () => {
    this.props.enqueueSnackbar(
      "Unable to fetch strip priority shipments data",
      {
        variant: "error",
      }
    );
    this.setState({
      shipmentsData: [],
      proIdList: [],
      errorMessage: appConstants.GRID_STATUS.ERROR,
    });
  };

  onCellClicked = (params) => {
    this.setState({
      proIdList: this.gridApi
        .getSelectedRows()
        .map((shipment) => shipment.proId),
    });
    if (
      this.hasProImagesURL() &&
      params.column.colId === "proId" &&
      this.state.proIdList.length === 1
    ) {
      this.updateModalVisibility(true);
    }
  };

  updateModalVisibility = (showProImagesPopup) => {
    this.setState({
      showProImagesPopup,
    });
  };

  constructStatusBar = (filteredData) => {
    const statusBarData = [
      {
        label: `${filteredData.length || 0} Total Shipments`,
        filterCondition: "",
      },
    ];
    return statusBarData;
  };

  render() {
    const { classes, toggleMaximize, isMaximized } = this.props;
    const { config } = this.gridConfig;
    const { shipmentsData } = this.state;
    const selectedTrailersShipments = this.getTrailersShipments();
    return (
      <div
        className={classes.shipmentContainer}
      >
        <Header
          exportCSV={this.exportCSV}
          title="Shipments"
          updateQuickFilterText={this.updateQuickFilterText}
          gridApi={this.gridApi}
          toggleMaximize={toggleMaximize}
          isMaximized={isMaximized}
        />
        <div className={`${classes.dataGrid} ${isMaximized ? '' : classes.shipmentGridHeight}`}>
          <DataGrid
            columnDefs={config.columns}
            config={config}
            rowData={
              selectedTrailersShipments.length === 0
                ? shipmentsData
                : selectedTrailersShipments
            }
            enableSorting={config.enableSorting}
            onGridReady={this.onGridReady}
            quickFilterText={this.state.quickFilterText}
            rowSelection={this.gridConfig.config.rowSelection}
            rowDeselection={this.gridConfig.config.rowDeselection}
            onCellClicked={this.onCellClicked}
            context={this.gridContext}
            showErrorMessage={this.state.errorMessage}
            panelLabel={(filteredData) => (
              <StatusBar data={this.constructStatusBar(filteredData)} />
            )}
          />
          <Suspense fallback={<AppLoading />}>
            {this.state.showProImagesPopup && (
              <ProImages
                proNumber={this.state.proIdList[0]}
                show={this.state.showProImagesPopup}
                onClose={() => {
                  this.updateModalVisibility(false);
                }}
              />
            )}
          </Suspense>
        </div>
      </div>
    );
  }
}
export default compose(
  withStyles(styles),
  withUserProfile,
  withAppConfig,
  withSnackbar
)(Shipments);
